import { useEffect, useState } from "react";
import { getGleanHelperConfig, type GleanHelperConfig } from "../api/gleanHandoff";
import { ServerError } from "../state/denialErrors";

const MAX_CONFIG_ATTEMPTS = 3;
const DEFAULT_RETRY_SECONDS = 5;
const initialState: HelperConfigState = { config: null, loaded: false, error: false };

interface HelperConfigState {
  config: GleanHelperConfig | null;
  loaded: boolean;
  error: boolean;
}

/** Refresh temporary release-discovery failures without restarting the handoff. */
export function useGleanHelperConfig(enabled = true) {
  const [state, setState] = useState<HelperConfigState>(initialState);
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    let timer: number | undefined;
    let attempts = 0;
    setState(initialState);

    async function load() {
      attempts += 1;
      try {
        const config = await getGleanHelperConfig(controller.signal);
        if (controller.signal.aborted) return;
        const retryable = config.helper_install_status === "retryable_error";
        if (retryable && attempts < MAX_CONFIG_ATTEMPTS) {
          // Other platforms can already use their independently configured URL.
          setState({ config, loaded: false, error: false });
          timer = window.setTimeout(() => void load(), (config.helper_install_retry_after_seconds ?? DEFAULT_RETRY_SECONDS) * 1_000);
        } else {
          setState({ config, loaded: true, error: retryable || config.helper_install_status === "error" });
        }
      } catch (error) {
        if (controller.signal.aborted) return;
        const retryable = error instanceof TypeError || (error instanceof ServerError && error.retryable);
        if (retryable && attempts < MAX_CONFIG_ATTEMPTS) {
          timer = window.setTimeout(() => void load(), DEFAULT_RETRY_SECONDS * 1_000);
        } else {
          setState((current) => ({ ...current, loaded: true, error: true }));
        }
      }
    }

    void load();
    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [enabled, revision]);

  return { ...state, retry: () => setRevision((value) => value + 1) };
}
